import type { DailyCardId } from "../content/dailycards";

/** Base Clarity awards. Multipliers/flat bonuses from the active daily card
    are applied on top via applyDailyCardMultiplier — see dailycards.ts for
    what each card actually does. */
export const CLARITY = {
  warmup: 15,
  phonationPB: 25,
  plosive: 20, // scaled 0..20 by timing accuracy at the call site
  reviewGood: 12,
  reviewHard: 6,
  reviewAgain: 3, // an honest miss never pays zero
  newCardIntroduced: 8,
  firstMastery: 30,
  finisher: 25, // scaled 0..25 by score at the call site
  sessionComplete: 40,
  doubleSession: 60,
  weeklyTargetMet: 120,
} as const;

export const CLEAN_ROLL_MIN = 0;
export const CLEAN_ROLL_MAX = 12;

/** mulberry32 — small, fast, deterministic. Seeded per (profile.seed, day)
    so the clean-finish roll is stable if a session is reloaded mid-way. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function cleanRoll(rng: () => number): number {
  return Math.round(CLEAN_ROLL_MIN + rng() * (CLEAN_ROLL_MAX - CLEAN_ROLL_MIN));
}

/** Global multipliers only — flat bonuses (cold-open, marathon) and
    per-item bonuses (perfectionist, double-finisher) are applied by the
    session builder/runner, which knows which item is "the finisher" etc. */
export function applyDailyCardMultiplier(baseClarity: number, cardId: DailyCardId | null | undefined): number {
  switch (cardId) {
    case "sound-focus":
      return Math.round(baseClarity * 1.2);
    case "speed-trial":
      return Math.round(baseClarity * 1.5);
    case "polyglot":
      return Math.round(baseClarity * 1.25);
    case "long-player":
      return Math.round(baseClarity * 1.6);
    case "golden-take":
      return baseClarity * 3;
    default:
      return baseClarity;
  }
}
